import { NotificationList } from 'components/common/NotificationList'
import { put, takeLatest } from 'redux-saga/effects'
import { makeGetRequest, makePostRequest, makePutRequest, makeDeleteRequest } from 'utils/api'
import { formatResponse, formatErrorMessage } from 'utils/formatting'
import { fromBoardItems, toTask } from 'utils/serializers'
import { toBoardData } from './serializers'

import * as ACT from './actions'
import { PUSH_NOTIFICATION } from 'actions/actionTypes'
import { refreshNotifications } from 'utils/helpers'
import { isAdmin } from 'utils/authService'

function* fetchProposalsRowData() {
  try {
    const url = '/v1/proposals'
    const rowData = yield makeGetRequest(url)
    yield put({
      type: ACT.PROPOSALS_ROW_DATA_FETCH_SUCCESS,
      payload: {
        rowData
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Opportunities',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchProposalsBoardRowData(action) {
  const { filters } = action.payload
  const { endDate, startDate } = filters
  try {
    const url = `/v1/proposals/board/${startDate}/${endDate}`
    const data = yield makeGetRequest(url)

    const rowData = fromBoardItems(data.content[0])
    yield put({
      type: ACT.PROPOSALS_BOARD_ROW_DATA_FETCH_SUCCESS,
      payload: {
        rowData,
        paginatedBoard: toBoardData(rowData)
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Proposals',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* createTask(action) {
  const { proposalId, task, proposalStatus } = action.payload
  try {
    const url = '/v1/proposals/scheduleTask'
    const response = yield makePostRequest(url, toTask(task, { proposalId }))
    response.id = String(response.id)

    yield put({
      type: ACT.CREATE_PROPOSAL_BOARD_TASK_SUCCESS,
      payload: {
        proposals: response,
        status: proposalStatus,
        id: proposalId
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task created successfully',
        level: 'success'
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue creating Task for Proposal',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* updateTask(action) {
  const { task, proposalStatus } = action.payload
  try {
    const url = `/v1/proposals/task/${task.id}`
    const taskObj = toTask(task)
    taskObj.id = task.id
    taskObj.proposalId = task.proposals[0]

    const proposal = yield makePutRequest(url, taskObj)
    task.id = String(task.id)

    yield put({
      type: ACT.UPDATE_BOARD_PROPOSAL_TASK_SUCCESS,
      payload: {
        proposal,
        status: proposalStatus
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task updated successfully',
        level: 'success'
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue updating Task',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* deleteTask(action) {
  const { task, proposalStatus, proposalId } = action.payload
  try {
    const url = `/v1/proposals/${proposalId}/task/${task.id}`
    const proposal = yield makeDeleteRequest(url)

    yield put({
      type: ACT.DELETE_BOARD_PROPOSAL_TASK_SUCCESS,
      payload: {
        proposal,
        status: proposalStatus
      }
    })

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task deleted successfully',
        level: 'success'
      }
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue deleting Task',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* markTaskCompleted(action) {
  try {
    const { id, proposalStatus, proposalId } = action.payload
    const url = `/v1/tasks/${id}/markcompleted`
    const task = yield makePutRequest(url)

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task was marked as completed!',
        level: 'success'
      }
    })
    yield put({
      type: ACT.PROPOSAL_BOARD_TASK_MARK_COMPLETED_SUCCESS,
      payload: {
        id,
        proposalStatus,
        proposalId
      }
    })

    yield refreshNotifications(put)
    yield put({
      type: ACT.PROPOSAL_TASK_MARK_COMPLETED_SUCCESS,
      payload: {
        task
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue marking the Task as completed',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* markTaskNotCompleted(action) {
  try {
    const { id, category, proposalStatus, proposalId } = action.payload
    const url = `/v1/tasks/${id}/marknotcompleted`
    const task = yield makePutRequest(url)

    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'Task was marked as not completed!',
        level: 'success'
      }
    })
    yield put({
      type: ACT.PROPOSAL_BOARD_TASK_MARK_NOT_COMPLETED_SUCCESS,
      payload: {
        id,
        category,
        proposalStatus,
        proposalId
      }
    })
    yield put({
      type: ACT.PROPOSAL_TASK_MARK_COMPLETED_SUCCESS,
      payload: {
        task
      }
    })
    yield put({
      type: ACT.PROPOSAL_TASK_MARK_NOT_COMPLETED_SUCCESS
    })

    yield refreshNotifications(put)
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue marking the Task as not completed',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchProposalTemplates(action) {
  try {
    const { templateType } = action.payload
    let url = '/v1/proposaltemplates'
    if (templateType === 'HTML') {
      url = '/v1/proposaltemplates/html'
    }
    const templates = yield makeGetRequest(url)
    yield put({
      type: ACT.PROPOSALS_TEMPLATES_FETCH_SUCCESS,
      payload: {
        templates
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Proposal Templates',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchProposalsBoardByUserRowData(action) {
  const { userId, filters } = action.payload
  const { startDate, endDate } = filters

  try {
    let url = `/v1/proposals/board/${userId}/${startDate}/${endDate}`
    if (!isAdmin() || userId === -1) {
      url = `/v1/proposals/board/${startDate}/${endDate}`
    }

    const rowData = yield makeGetRequest(url)

    yield put({
      type: ACT.PROPOSALS_BOARD_FOR_USER_FETCH_SUCCESS
    })

    yield put({
      type: ACT.PROPOSALS_BOARD_ROW_DATA_FETCH_SUCCESS,
      payload: {
        rowData: fromBoardItems(rowData.content[0]),
        paginatedBoard: toBoardData(fromBoardItems(rowData.content[0]))
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Proposals',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

function* fetchProposalsListByUserRowData(action) {
  const { userId } = action.payload
  try {
    let url = `/v1/proposals/users/${userId}`
    if (!isAdmin() || userId === -1) {
      url = '/v1/proposals'
    }
    const rowData = yield makeGetRequest(url)

    yield put({
      type: ACT.PROPOSALS_LIST_FOR_USER_FETCH_SUCCESS
    })

    yield put({
      type: ACT.PROPOSALS_ROW_DATA_FETCH_SUCCESS,
      payload: {
        rowData
      }
    })
  } catch (error) {
    yield put({
      type: PUSH_NOTIFICATION,
      payload: {
        title: 'There was an issue fetching Proposals',
        message: <NotificationList lines={formatResponse(formatErrorMessage(error))} />,
        level: 'error'
      }
    })
  }
}

const proposalsSagas = [
  takeLatest(ACT.PROPOSALS_ROW_DATA_FETCH_REQUESTED, fetchProposalsRowData),
  takeLatest(ACT.PROPOSALS_BOARD_ROW_DATA_FETCH_REQUESTED, fetchProposalsBoardRowData),
  takeLatest(ACT.CREATE_PROPOSAL_BOARD_TASK_REQUESTED, createTask),
  takeLatest(ACT.UPDATE_BOARD_PROPOSAL_TASK_REQUESTED, updateTask),
  takeLatest(ACT.DELETE_BOARD_PROPOSAL_TASK_REQUESTED, deleteTask),
  takeLatest(ACT.PROPOSAL_BOARD_TASK_MARK_COMPLETED_REQUESTED, markTaskCompleted),
  takeLatest(ACT.PROPOSAL_BOARD_TASK_MARK_NOT_COMPLETED_REQUESTED, markTaskNotCompleted),
  takeLatest(ACT.PROPOSALS_TEMPLATES_FETCH_REQUESTED, fetchProposalTemplates),
  takeLatest(ACT.PROPOSALS_BOARD_FOR_USER_FETCH_REQUESTED, fetchProposalsBoardByUserRowData),
  takeLatest(ACT.PROPOSALS_LIST_FOR_USER_FETCH_REQUESTED, fetchProposalsListByUserRowData)
]

export default proposalsSagas
